
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

// Define allowed status types to match the database constraint
type StatusType = 'pending' | 'approved' | 'denied' | 'resolved';

export const useConversationStatus = (
  conversationId: string,
  initialStatus: string = 'pending'
) => {
  const [status, setStatus] = useState<StatusType>((initialStatus || 'pending').toLowerCase() as StatusType);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [optimisticStatus, setOptimisticStatus] = useState<StatusType | null>(null);
  const { toast } = useToast();

  // Reset error when status changes
  useEffect(() => {
    setErrorMessage(null);
  }, [status]);

  // Validate the status value against allowed types
  const isValidStatus = (value: string): value is StatusType => {
    return ['pending', 'approved', 'denied', 'resolved'].includes(value);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === status) return;
    
    // Validate the new status
    if (!isValidStatus(newStatus)) {
      setErrorMessage(`Invalid status: ${newStatus}. Must be one of: pending, approved, denied, resolved.`);
      return;
    }
    
    setIsUpdating(true);
    setErrorMessage(null);
    
    // Optimistically update the UI
    setOptimisticStatus(newStatus as StatusType);
    
    try {
      const { error } = await supabase
        .from('refund_requests')
        .update({ status: newStatus })
        .eq('id', conversationId);
      
      if (error) throw error;
      
      setStatus(newStatus as StatusType);
      toast({
        title: "Status updated",
        description: `Request status changed to ${newStatus}`,
      });
    } catch (error: any) {
      console.error("Error updating status:", error);
      
      // Reset optimistic update
      setOptimisticStatus(null);
      
      // Set specific error message based on the error
      if (error.message?.includes('violates check constraint')) {
        setErrorMessage(`Status '${newStatus}' is not allowed. Valid options are: pending, approved, denied, resolved.`);
      } else {
        setErrorMessage(error.message || "Failed to update status. Please try again.");
      }
      
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update status. Please try again.",
      });
    } finally {
      setIsUpdating(false);
      // Clear optimistic status after a short delay to ensure smooth transition
      setTimeout(() => setOptimisticStatus(null), 300);
    }
  };

  // Display current status with optimistic update if applicable
  const displayStatus = optimisticStatus || status;

  return {
    status,
    displayStatus,
    isUpdating,
    errorMessage,
    handleStatusChange
  };
};
